import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import vendorProfileService from "../../services/vendorProfileService";

// Async thunks for vendor profile operations
export const fetchVendorProfiles = createAsyncThunk(
  "vendorProfile/fetchVendorProfiles",
  async (params, { rejectWithValue }) => {
    try {
      const response = await vendorProfileService.getAllVendorProfiles(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch vendor profiles"
      );
    }
  }
);

export const fetchVendorProfileStats = createAsyncThunk(
  "vendorProfile/fetchVendorProfileStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await vendorProfileService.getVendorProfileStats();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch vendor profile stats"
      );
    }
  }
);

export const fetchVendorProfileById = createAsyncThunk(
  "vendorProfile/fetchVendorProfileById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await vendorProfileService.getVendorProfileById(id);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch vendor profile"
      );
    }
  }
);

export const createVendorProfile = createAsyncThunk(
  "vendorProfile/createVendorProfile",
  async (vendorProfileData, { rejectWithValue }) => {
    try {
      const response = await vendorProfileService.createVendorProfile(
        vendorProfileData
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create vendor profile"
      );
    }
  }
);

export const updateVendorProfile = createAsyncThunk(
  "vendorProfile/updateVendorProfile",
  async ({ id, vendorProfileData }, { rejectWithValue }) => {
    try {
      const response = await vendorProfileService.updateVendorProfile(
        id,
        vendorProfileData
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update vendor profile"
      );
    }
  }
);

export const deleteVendorProfile = createAsyncThunk(
  "vendorProfile/deleteVendorProfile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await vendorProfileService.deleteVendorProfile(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete vendor profile"
      );
    }
  }
);

export const approveVendorProfile = createAsyncThunk(
  "vendorProfile/approveVendorProfile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await vendorProfileService.approveVendorProfile(id);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to approve vendor profile"
      );
    }
  }
);

export const rejectVendorProfile = createAsyncThunk(
  "vendorProfile/rejectVendorProfile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await vendorProfileService.rejectVendorProfile(id);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject vendor profile"
      );
    }
  }
);

const vendorProfileSlice = createSlice({
  name: "vendorProfile",
  initialState: {
    vendorProfiles: [],
    currentVendorProfile: null,
    stats: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
    },
    filters: {
      status: undefined,
      business_name: "",
      business_email: "",
      payment_terms: "",
    },
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: undefined,
        business_name: "",
        business_email: "",
        payment_terms: "",
      };
    },
    setCurrentVendorProfile: (state, action) => {
      state.currentVendorProfile = action.payload;
    },
    clearCurrentVendorProfile: (state) => {
      state.currentVendorProfile = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch vendor profiles
      .addCase(fetchVendorProfiles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVendorProfiles.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle case where action.payload might be undefined
        if (!action.payload) {
          state.vendorProfiles = [];
          state.pagination = { page: 1, limit: 10, total: 0 };
          state.stats = null;
          return;
        }
        
        const responseData = action.payload.data || action.payload;
        state.vendorProfiles =
          responseData.vendorProfiles || responseData || [];
        state.pagination = responseData.pagination || {
          page: 1,
          limit: 10,
          total: Array.isArray(responseData.vendorProfiles || responseData)
            ? (responseData.vendorProfiles || responseData).length
            : 0,
        };
        state.stats = responseData.stats;
      })
      .addCase(fetchVendorProfiles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch vendor profile stats
      .addCase(fetchVendorProfileStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVendorProfileStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload.data || action.payload;
      })
      .addCase(fetchVendorProfileStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch vendor profile by ID
      .addCase(fetchVendorProfileById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVendorProfileById.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload.data || action.payload;
        state.currentVendorProfile = responseData.vendorProfile || responseData;
      })
      .addCase(fetchVendorProfileById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create vendor profile
      .addCase(createVendorProfile.pending, (state) => {
        state.isCreating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createVendorProfile.fulfilled, (state, action) => {
        state.isCreating = false;
        const responseData = action.payload.data || action.payload;
        const newVendorProfile = responseData.vendorProfile || responseData;
        if (newVendorProfile) {
          state.vendorProfiles.unshift(newVendorProfile);
        }
        console.log("Create vendor response:", action.payload); // Debug log
        state.success = action.payload.message || "Vendor created successfully";
      })
      .addCase(createVendorProfile.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })

      // Update vendor profile
      .addCase(updateVendorProfile.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateVendorProfile.fulfilled, (state, action) => {
        state.isUpdating = false;
        const responseData = action.payload.data || action.payload;
        const updatedVendorProfile = responseData.vendorProfile || responseData;
        const index = state.vendorProfiles.findIndex(
          (profile) => profile.id === updatedVendorProfile.id
        );
        if (index !== -1) {
          state.vendorProfiles[index] = updatedVendorProfile;
        }
        if (state.currentVendorProfile?.id === updatedVendorProfile.id) {
          state.currentVendorProfile = updatedVendorProfile;
        }
        console.log("Update vendor response:", action.payload); // Debug log
        state.success = action.payload.message || "Vendor updated successfully";
      })
      .addCase(updateVendorProfile.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // Delete vendor profile
      .addCase(deleteVendorProfile.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteVendorProfile.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.vendorProfiles = state.vendorProfiles.filter(
          (profile) => profile.id !== action.payload.id
        );
        state.success = action.payload.response.message;
      })
      .addCase(deleteVendorProfile.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      })

      // Approve vendor profile
      .addCase(approveVendorProfile.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(approveVendorProfile.fulfilled, (state, action) => {
        state.isUpdating = false;
        const responseData = action.payload.data || action.payload;
        const updatedVendorProfile = responseData.vendorProfile || responseData;
        const index = state.vendorProfiles.findIndex(
          (profile) => profile.id === updatedVendorProfile.id
        );
        if (index !== -1) {
          state.vendorProfiles[index] = updatedVendorProfile;
        }
        if (state.currentVendorProfile?.id === updatedVendorProfile.id) {
          state.currentVendorProfile = updatedVendorProfile;
        }
        state.success = action.payload.message;
      })
      .addCase(approveVendorProfile.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // Reject vendor profile
      .addCase(rejectVendorProfile.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(rejectVendorProfile.fulfilled, (state, action) => {
        state.isUpdating = false;
        const responseData = action.payload.data || action.payload;
        const updatedVendorProfile = responseData.vendorProfile || responseData;
        const index = state.vendorProfiles.findIndex(
          (profile) => profile.id === updatedVendorProfile.id
        );
        if (index !== -1) {
          state.vendorProfiles[index] = updatedVendorProfile;
        }
        if (state.currentVendorProfile?.id === updatedVendorProfile.id) {
          state.currentVendorProfile = updatedVendorProfile;
        }
        state.success = action.payload.message;
      })
      .addCase(rejectVendorProfile.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  setFilters,
  clearFilters,
  setCurrentVendorProfile,
  clearCurrentVendorProfile,
} = vendorProfileSlice.actions;

export default vendorProfileSlice.reducer;
